/// <reference types="../../src/liburi" />
import * as URI from 'spotify-liburi';
import { LRUCache } from '@spotify-internal/linkedlist';
const DEFAULT_CACHE_SIZE = 20;
export class MetadataDecorator {
    constructor(options) {
        this._transport = options.transport;
        this._metadataCache = new LRUCache(options.cacheSize || DEFAULT_CACHE_SIZE);
    }
    static create(options) {
        return new MetadataDecorator(options);
    }
    _decorateTrackWindow(trackWindow) {
        const cache = this._metadataCache;
        const decoratedWindow = {
            current_track: null,
            next_tracks: [],
            previous_tracks: [],
        };
        let len;
        let trackData;
        let sparseTrackData;
        if (trackWindow.current_track) {
            trackData =
                cache &&
                    trackWindow.current_track &&
                    'uri' in trackWindow.current_track &&
                    cache.get(trackWindow.current_track.uri);
            decoratedWindow.current_track = trackData || trackWindow.current_track;
        }
        len = trackWindow.next_tracks.length;
        while (len--) {
            sparseTrackData = trackWindow.next_tracks[len];
            trackData =
                cache &&
                    sparseTrackData &&
                    'uri' in sparseTrackData &&
                    cache.get(sparseTrackData.uri);
            decoratedWindow.next_tracks[len] = trackData || sparseTrackData;
        }
        len = trackWindow.previous_tracks.length;
        while (len--) {
            sparseTrackData = trackWindow.previous_tracks[len];
            trackData =
                cache &&
                    sparseTrackData &&
                    'uri' in sparseTrackData &&
                    cache.get(sparseTrackData.uri);
            decoratedWindow.previous_tracks[len] = trackData || sparseTrackData;
        }
        return decoratedWindow;
    }
    _buildTrackList(trackWindow) {
        const list = [];
        const map = {};
        const tracks = [
            trackWindow.current_track,
            ...trackWindow.next_tracks,
            ...trackWindow.previous_tracks,
        ];
        for (const track of tracks) {
            if (!track || map[track.uri]) {
                continue;
            }
            list.push(track.uri);
        }
        return list;
    }
    _cacheMetadata(list) {
        // Build two id arrays, one for episodes and one for tracks. These will
        // then be used to request the metadata for the track.
        const episodeIds = [];
        const trackIds = [];
        const cache = this._metadataCache;
        for (let i = 0, l = list.length; i < l; i++) {
            const item = list[i];
            if (cache.get(item)) {
                continue;
            }
            const uri = URI.from(item);
            if (!uri) {
                continue;
            }
            const id = URI.hexToId(uri.id);
            if (uri.type === URI.Type.TRACK) {
                trackIds.push(id);
            }
            else if (uri.type === URI.Type.EPISODE) {
                episodeIds.push(id);
            }
        }
        if (!episodeIds.length && !trackIds.length) {
            // All items are cached, so we can simply do the decoration.
            return Promise.resolve(true);
        }
        return Promise.all([
            this._requestMultiData('episodes', episodeIds),
            this._requestMultiData('tracks', trackIds),
        ]).then(([podcastResponse, trackResponse]) => {
            return !!podcastResponse || !!trackResponse;
        });
    }
    _requestMultiData(listType, ids) {
        if (!ids.length) {
            return Promise.resolve(false);
        }
        const cache = this._metadataCache;
        const url = [
            'https://@webapi/v1/',
            listType,
            '?ids=',
            ids.join(','),
            '&market=from_token',
        ].join('');
        return this._transport
            .request(url, {
            responseType: 'json',
        })
            .then((response) => {
            var _a;
            if (response.status !== 200) {
                return false;
            }
            // prepare map
            const items = (_a = response.body) === null || _a === void 0 ? void 0 : _a[listType];
            if (!items) {
                return false;
            }
            let len = items.length;
            while (len--) {
                let item = items[len];
                if (!item) {
                    continue;
                }
                if (listType === 'episodes') {
                    item = this._formatEpisodeData(item);
                }
                else {
                    item.track_type = 'audio';
                    item.media_type = 'audio';
                }
                if (cache) {
                    cache.set(item.uri, item);
                }
                if (cache && item.linked_from && item.linked_from.uri) {
                    cache.set(item.linked_from.uri, item);
                }
            }
            return true;
        });
    }
    _formatEpisodeData(item) {
        // The value `item.show.media_type` can be `video`, `audio` or `mixed` from WebAPI.
        // Cast it to `audio` or `video` for the sake of compatibility.
        const mediaType = (item.show.media_type === 'audio' ? 'audio' : 'video');
        const track = {
            id: item.id,
            uri: item.uri,
            type: 'episode',
            media_type: mediaType,
            track_type: item.show.media_type,
            name: item.name,
            artists: [
                {
                    uri: item.show.uri,
                    name: item.show.publisher,
                },
            ],
            album: {
                uri: item.show.uri,
                name: item.show.name,
                images: item.images,
            },
            duration_ms: item.duration_ms || 0,
            is_playable: true,
        };
        return track;
    }
    formatLocalTrack(uriObj) {
        if (!uriObj || uriObj.type !== URI.Type.LOCAL) {
            return null;
        }
        const track = {
            uri: uriObj.toURI(),
            type: 'local',
            media_type: 'audio',
            track_type: 'audio',
            name: uriObj.track,
            artists: [
                {
                    uri: URI.localArtistURI(uriObj.artist).toURI(),
                    name: uriObj.artist,
                },
            ],
            album: {
                uri: URI.localAlbumURI(uriObj.artist, uriObj.album).toURI(),
                name: uriObj.album,
                images: [{ url: '' }],
            },
            // Local-track duration is in seconds
            duration_ms: uriObj.duration ? uriObj.duration * 1000 : 0,
            is_playable: false,
        };
        return track;
    }
    formatAd(uriObj, item) {
        if (!uriObj ||
            (uriObj.type !== URI.Type.AD && uriObj.type !== URI.Type.INTERRUPTION)) {
            return null;
        }
        if (!item || !item.metadata) {
            return null;
        }
        const metadata = item.metadata;
        const track = {
            id: uriObj.id,
            uri: uriObj.toURI(),
            type: uriObj.type,
            media_type: 'audio',
            track_type: 'audio',
            name: metadata.advertiser,
            artists: [
                {
                    uri: metadata.click_url,
                    name: metadata.buttonMessage || metadata.advertiser,
                },
            ],
            album: {
                uri: metadata.click_url,
                name: metadata.advertiser,
                images: [{ url: metadata.image_url }],
            },
            // duration in ad metadata is reported as a String, so this is needed:
            duration_ms: parseInt(metadata.duration, 10),
            is_playable: false,
        };
        return track;
    }
    decorateTrackWindow(trackWindow, trackList) {
        const list = trackList !== null && trackList !== void 0 ? trackList : this._buildTrackList(trackWindow);
        return this._cacheMetadata(list).then((result) => {
            if (!result) {
                return trackWindow;
            }
            return this._decorateTrackWindow(trackWindow);
        });
    }
}
//# sourceMappingURL=metadata_decorator.js.map